import React, {
  useCallback,
  useEffect, useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import MaiaIconImage from '@/media/MaiaIA/iconImage.png';
import IconAnimation from '@/components/BannerAI/media/AiAnimation.webp';
import listeningImage from '@/media/MaiaIA/listening.png';
import * as Styles from './NewAiHeaderButton.styles';
import {
  IconStarMoving, IconAudioRecord, IconArrow, Button,
  IconClose,
} from 'gatorcomponents';
import { OPTIONS } from './NewAiHeaderButton.types';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { AudioRecording } from '../../AiChat/components/AudioRecording';
import { useDispatch, useSelector } from 'react-redux';
import { useAiChat } from '@/hooks/AiChat';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { ATIVACAO_N8N_INHOUSE_URLS } from '@/config/GrowthBook/constants';
import { WSS_ROUTES } from '../../AiChat/AiChat.enum';
import { merlinActions } from '@/redux/modules';
import { useResize } from '@/hooks/useResize';
import useLocale from '@/hooks/useLocale';

const NewAiHeaderButton = ({
  hideOnMobile,
  hideOnDesktop,
}) => {
  const dispatch = useDispatch();
  const { triggerExternalMessage, triggerExternalAudioBlob } = useAiChat();
  const fullScreenAudio = false;
  const { recording, handleStartRecording, handleStopRecording } = useAudioRecorder({
    onStop: audioBlob => triggerExternalAudioBlob(audioBlob, fullScreenAudio),
  });
  const { aichat: generalChatLocale } = useLocale();
  const { headerLocale } = generalChatLocale;
  const view = useResize();
  const isMobile = view.width < 960;

  const [iconImage, setIconImage] = useState(MaiaIconImage || IconAnimation);
  const [focused, setFocused] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [typedMessage, setTypedMessage] = useState('');

  const { loadingTranscription } = useSelector(state => state.merlin);
  const wssToggleRoutes = useFeatureValue(ATIVACAO_N8N_INHOUSE_URLS);

  const inputRef = useRef(null);
  const inputFieldRef = useRef(null);
  const isRecording = recording || loadingTranscription;

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    const route = wssToggleRoutes.general || WSS_ROUTES.GENERAL;
    dispatch(merlinActions.inhouse.setRoute(route));
    triggerExternalMessage({ message: typedMessage, fullScreen: false });
    setTypedMessage('');
  }, [typedMessage, dispatch, triggerExternalMessage, wssToggleRoutes]);

  const updateImage = useCallback(() => {
    const eightSeconds = 8000;
    setTimeout(() => {
      setIconImage(MaiaIconImage);
    }, eightSeconds);
  }, []);

  useEffect(() => {
    updateImage();
  }, [updateImage]);


  const handleFocus = () => {
    setFocused(prev => !prev);
  };

  const keyListener = useCallback((event) => {
    if (event.key === 'Escape') {
      setFocused(false);
      if (inputFieldRef.current) {
        inputFieldRef.current.blur();
      }
    }

    if (event.key === 'Enter' && typedMessage) {
      handleSubmit(event);
    }
  }, [typedMessage, handleSubmit]);

  useEffect(() => {
    document.addEventListener('keydown', keyListener);
    return () => {
      document.removeEventListener('keydown', keyListener);
    };
  }, [keyListener]);

  const hideBodyOverflow = useCallback(() => {
    if (focused) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [focused]);

  useEffect(() => {
    hideBodyOverflow();

    return () => {
      hideBodyOverflow();
    };
  }, [hideBodyOverflow]);

  const handleOptionAction = ({ value, message }) => {
    if (value === OPTIONS.MORE) {
      setShowMoreOptions(prev => !prev);
      return;
    }

    const setRoute = (route) => {
      dispatch(merlinActions.inhouse.setRoute(route));
    };

    const routes = {
      [OPTIONS.CREATE_EMAIL]: wssToggleRoutes.email || WSS_ROUTES.EMAIL,
      [OPTIONS.CONFIGURE_DOMAIN]: wssToggleRoutes.domain || WSS_ROUTES.DOMAIN,
      [OPTIONS.CREATE_SITE]: wssToggleRoutes.site || WSS_ROUTES.SITE,
      general: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
    };

    const handleSelection = () => {
      setRoute(routes[value] || routes.general);
      triggerExternalMessage({ message, fullScreen: false });
    };

    return handleSelection();
  };

  if (hideOnMobile && isMobile) {
    return null;
  }

  if (hideOnDesktop && !isMobile) {
    return null;
  }

  return (
    <>
      {((focused || isRecording) && !hideOnDesktop) && createPortal(
        <Styles.Overlay onClick={handleFocus} />,
        document.getElementById('navMenu') || document.body,
      )}

      {(focused || isRecording) && (
        <Styles.Overlay onClick={handleFocus} id="bodyOverlay" />
      )}

      <Styles.Wrapper $focused={focused}>
        <Styles.InputBorder ref={inputRef}>
          <Styles.InputWrapper>
            <Styles.Image src={isRecording ? listeningImage : iconImage} />

            {!isRecording && (
            <>
              <Styles.Input
                ref={inputFieldRef}
                onFocus={handleFocus}
                placeholder={headerLocale.talkToMaia}
                onChange={e => setTypedMessage(e.target.value)}
              />

              <Styles.ActionsButton $focused={focused} onClick={handleFocus}>
                <IconStarMoving color={focused ? 'primaryMedium' : 'lowDark'} />
                {headerLocale.actions}
              </Styles.ActionsButton>

              <Styles.ActionsButton onClick={handleStartRecording}>
                <IconAudioRecord />
              </Styles.ActionsButton>

              {typedMessage && (
                <Styles.SubmitWrapper>
                  <Button
                    type="submit"
                    variant="primary"
                    width="fitContent"
                    label={<IconArrow direction="right" />}
                    testId="submit"
                    onClick={handleSubmit}
                  />
                </Styles.SubmitWrapper>
              )}
            </>
            )}

            {isRecording && (
              <Styles.SubmitWrapper>
                <AudioRecording
                  onCancel={() => handleStopRecording({ cancelRecording: true })}
                  onSend={() => handleStopRecording({ cancelRecording: false })}
                  isRecording={recording}
                  transcribing={loadingTranscription}
                  locales={{
                    recordingLabel: headerLocale.recordingLabel,
                    transcribingLabel: headerLocale.transcribingLabel,
                  }}
                  whiteBackground
                />
              </Styles.SubmitWrapper>
            )}

          </Styles.InputWrapper>
        </Styles.InputBorder>

        {(focused && !typedMessage.length) && (
          <Styles.OptionsWrapper>
            {headerLocale.options.map(option => (!option.showMoreOptions || (option.showMoreOptions && !!showMoreOptions)) && (
              <Styles.Option
                key={option.label}
                role="button"
                onClick={() => handleOptionAction({ value: option.value, message: option.message })}
              >
                {option.Icon && <option.Icon size="sm2" />}
                {option.label}
              </Styles.Option>
            ))}
          </Styles.OptionsWrapper>
        )}

        {focused && (
          <Styles.CloseButtonWrapper>
            <Button
              variant="secondary"
              label={<IconClose size="md" />}
              onClick={handleFocus}
              width="fitContent"
            />
          </Styles.CloseButtonWrapper>
        )}
      </Styles.Wrapper>
    </>
  );
};

export default NewAiHeaderButton;
